<script setup lang="ts">
import Footer from '@/components/Footer.vue';
import Header from '@/components/Header.vue';
import { Head } from '@inertiajs/vue3';
import { Coins, Globe, Lock, PieChart, Shield, Target, TrendingUp, Users } from 'lucide-vue-next';

interface Feature {
    icon: any;
    title: string;
    description: string;
}

interface Distribution {
    label: string;
    percentage: number;
    color: string;
    description: string;
}

const tokenFeatures: Feature[] = [
    { icon: Shield, title: 'Utility Token', description: 'Not a security - provides access to platform features and services' },
    { icon: Lock, title: 'Fixed Supply', description: '1 billion $DS tokens - no additional minting possible' },
    { icon: TrendingUp, title: 'Deflationary', description: 'Token burning mechanism reduces supply over time' },
    { icon: Users, title: 'Community Driven', description: 'Future DAO governance without ownership rights' },
];

const distribution: Distribution[] = [
    { label: 'Public Sale', percentage: 50, color: 'from-primary to-primary/60', description: 'Infrastructure, marketing, expansion' },
    { label: 'Development Fund', percentage: 20, color: 'from-accent to-accent/60', description: '24-month release schedule' },
    { label: 'Team', percentage: 15, color: 'from-secondary to-secondary/60', description: '6-month cliff, 18-month vesting' },
    { label: 'Partners & Integrations', percentage: 10, color: 'from-muted to-muted/60', description: 'Strategic alliances' },
    { label: 'Community & Airdrops', percentage: 5, color: 'from-primary/40 to-accent/40', description: 'Community engagement' },
];

const useCases: Feature[] = [
    { icon: Target, title: 'Premium Services', description: 'Extended storage, version history access, and priority encryption' },
    { icon: Shield, title: 'Document Certification', description: 'Proof-of-Authenticity services and secure verification' },
    { icon: Users, title: 'Loyalty Programs', description: 'User engagement rewards and community incentives' },
    { icon: Globe, title: 'Transaction Discounts', description: 'Reduced fees and access to exclusive features' },
];
</script>

<template>
    <Head title="Tokenomics">
        <meta
            head-key="description"
            name="description"
            content="$DS Tokenomics. DigitalSafe Utility Token - Powering the Future of Secure Digital Asset Management"
        />
        <meta
            name="keywords"
            content="Digital Safe, $DS Tokenomics, Token Overview, Token Distribution, Token Use Cases, Regulatory Compliance, Development Status"
        />
    </Head>
    <div class="min-h-screen bg-background">
        <Header />
        <main class="pt-20">
            <!-- Hero Section -->
            <section class="relative overflow-hidden py-20">
                <div class="bg-gradient-subtle absolute inset-0 opacity-50"></div>
                <div class="relative z-10 container mx-auto max-w-4xl px-4 text-center">
                    <h1 class="mb-6 text-4xl font-bold md:text-6xl">
                        <span class="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">$DS</span> Tokenomics
                    </h1>
                    <p class="mb-8 text-lg leading-relaxed text-muted-foreground md:text-xl">
                        DigitalSafe Utility Token - Powering the Future of Secure Digital Asset Management
                    </p>
                    <div class="glass-card inline-block px-6 py-3">
                        <span class="font-semibold text-primary">We're working on it, more information coming soon.</span>
                    </div>
                </div>
            </section>

            <!-- Token Overview -->
            <section class="py-20">
                <div class="container mx-auto max-w-6xl px-4">
                    <div class="mb-16 text-center">
                        <h2 class="mb-4 text-3xl font-bold md:text-4xl">Token <span class="text-primary">Overview</span></h2>
                        <p class="text-lg text-muted-foreground">
                            The $DS token is designed as a utility tool that supports platform functionalities
                        </p>
                    </div>

                    <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                        <div
                            v-for="(feature, index) in tokenFeatures"
                            :key="index"
                            class="glass-card glow-hover hover:glow p-6 text-center transition-all duration-300"
                        >
                            <div class="glow mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-hero">
                                <component :is="feature.icon" class="h-8 w-8 text-primary-foreground" />
                            </div>
                            <h3 class="mb-3 text-lg font-semibold">{{ feature.title }}</h3>
                            <p class="text-sm leading-relaxed text-muted-foreground">{{ feature.description }}</p>
                        </div>
                    </div>

                    <!-- Key Stats -->
                    <div class="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
                        <div class="glass-card p-8 text-center">
                            <div
                                class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent"
                            >
                                <Coins class="h-10 w-10 text-primary-foreground" />
                            </div>
                            <h3 class="mb-2 text-3xl font-bold">1B</h3>
                            <p class="text-muted-foreground">Total Supply</p>
                        </div>
                        <div class="glass-card p-8 text-center">
                            <div
                                class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-accent to-secondary"
                            >
                                <Lock class="h-10 w-10 text-accent-foreground" />
                            </div>
                            <h3 class="mb-2 text-3xl font-bold">0</h3>
                            <p class="text-muted-foreground">Additional Minting</p>
                        </div>
                        <div class="glass-card p-8 text-center">
                            <div
                                class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-secondary to-primary"
                            >
                                <TrendingUp class="h-10 w-10 text-secondary-foreground" />
                            </div>
                            <h3 class="mb-2 text-3xl font-bold">Deflationary</h3>
                            <p class="text-muted-foreground">Token Burns</p>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Distribution Section -->
            <section class="bg-gradient-subtle py-20">
                <div class="container mx-auto max-w-6xl px-4">
                    <div class="mb-16 text-center">
                        <h2 class="mb-4 text-3xl font-bold md:text-4xl">Token <span class="text-primary">Distribution</span></h2>
                        <p class="text-lg text-muted-foreground">Balanced allocation between development, community, and growth</p>
                    </div>

                    <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
                        <!-- Distribution Chart -->
                        <div class="glass-card p-8">
                            <h3 class="mb-8 text-center text-2xl font-semibold">Distribution Breakdown</h3>
                            <div class="space-y-6">
                                <div v-for="(item, index) in distribution" :key="index" class="flex items-center space-x-4">
                                    <div
                                        :class="`h-12 w-12 bg-gradient-to-br ${item.color} glow flex flex-shrink-0 items-center justify-center rounded-lg`"
                                    >
                                        <span class="text-sm font-bold text-white">{{ item.percentage }}%</span>
                                    </div>
                                    <div class="flex-1">
                                        <div class="mb-1 flex items-center justify-between">
                                            <h4 class="font-semibold">{{ item.label }}</h4>
                                            <span class="font-bold text-primary">{{ item.percentage }}%</span>
                                        </div>
                                        <p class="text-sm text-muted-foreground">{{ item.description }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Visual Representation -->
                        <div class="glass-card p-8">
                            <div class="relative mx-auto h-64 w-64">
                                <div
                                    class="glow flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-primary via-accent to-secondary"
                                >
                                    <div class="flex h-32 w-32 items-center justify-center rounded-full bg-background">
                                        <div class="text-center">
                                            <PieChart class="mx-auto mb-2 h-12 w-12 text-primary" />
                                            <p class="text-2xl font-bold">$DS</p>
                                            <p class="text-sm text-muted-foreground">1B Tokens</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-8 text-center">
                                <h4 class="mb-4 font-semibold">Key Principles</h4>
                                <div class="space-y-2 text-sm text-muted-foreground">
                                    <p>• Transparent distribution</p>
                                    <p>• Vesting schedules for team tokens</p>
                                    <p>• Community-first approach</p>
                                    <p>• Sustainable growth model</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Use Cases & Additional Benefits -->
            <section class="py-20">
                <div class="container mx-auto max-w-6xl px-4">
                    <div class="mb-16 text-center">
                        <h2 class="mb-4 text-3xl font-bold md:text-4xl">Token <span class="text-primary">Use Cases</span></h2>
                        <p class="text-lg text-muted-foreground">How $DS tokens power the DigitalSafe ecosystem</p>
                    </div>

                    <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div
                            v-for="(useCase, index) in useCases"
                            :key="index"
                            class="glass-card hover:glow glow-hover p-8 transition-all duration-300"
                        >
                            <div class="flex items-start space-x-4">
                                <div class="glow flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-hero">
                                    <component :is="useCase.icon" class="h-8 w-8 text-primary-foreground" />
                                </div>
                                <div>
                                    <h3 class="mb-3 text-xl font-semibold">{{ useCase.title }}</h3>
                                    <p class="leading-relaxed text-muted-foreground">{{ useCase.description }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Additional Benefits -->
                    <div class="glass-card mt-12 p-8">
                        <h3 class="mb-6 text-center text-2xl font-semibold">Additional Benefits</h3>
                        <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
                            <div>
                                <h4 class="mb-4 font-semibold text-primary">Platform Features</h4>
                                <ul class="space-y-2 text-muted-foreground">
                                    <li>• Access to premium storage tiers</li>
                                    <li>• Priority customer support</li>
                                    <li>• Advanced security features</li>
                                    <li>• Early access to new features</li>
                                </ul>
                            </div>
                            <div>
                                <h4 class="mb-4 font-semibold text-primary">Community Benefits</h4>
                                <ul class="space-y-2 text-muted-foreground">
                                    <li>• Voting rights in governance decisions</li>
                                    <li>• Exclusive community events</li>
                                    <li>• Educational content access</li>
                                    <li>• Ambassador program participation</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Compliance Section -->
            <section class="bg-gradient-subtle py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <div class="mb-12 text-center">
                        <h2 class="mb-4 text-3xl font-bold md:text-4xl">Regulatory <span class="text-primary">Compliance</span></h2>
                        <p class="text-lg text-muted-foreground">Built with MiCA compliance and global regulations in mind</p>
                    </div>

                    <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div class="glass-card p-8">
                            <h3 class="mb-4 text-xl font-semibold text-primary">Compliance Features</h3>
                            <ul class="space-y-3 text-muted-foreground">
                                <li class="flex items-start space-x-2">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></div>
                                    <span>MiCA-compliant utility token design</span>
                                </li>
                                <li class="flex items-start space-x-2">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></div>
                                    <span>Geographic restrictions via geo-fencing</span>
                                </li>
                                <li class="flex items-start space-x-2">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></div>
                                    <span>DEX-only model initially</span>
                                </li>
                                <li class="flex items-start space-x-2">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></div>
                                    <span>Open-source and audited smart contracts</span>
                                </li>
                            </ul>
                        </div>

                        <div class="glass-card p-8">
                            <h3 class="mb-4 text-xl font-semibold text-accent">Important Disclaimers</h3>
                            <ul class="space-y-3 text-muted-foreground">
                                <li class="flex items-start space-x-2">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-accent"></div>
                                    <span>$DS is not a security or investment instrument</span>
                                </li>
                                <li class="flex items-start space-x-2">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-accent"></div>
                                    <span>No dividend rights or profit sharing</span>
                                </li>
                                <li class="flex items-start space-x-2">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-accent"></div>
                                    <span>User responsibility for local compliance</span>
                                </li>
                                <li class="flex items-start space-x-2">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-accent"></div>
                                    <span>Consult legal advisor before participation</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="glass-card mt-8 p-8 text-center">
                        <p class="text-muted-foreground">
                            <strong class="text-accent">Legal Notice:</strong> This document does not constitute legal, financial, or investment
                            advice. The $DS token is a utility token designed for use within the DigitalSafe platform ecosystem.
                        </p>
                    </div>
                </div>
            </section>

            <!-- Development Status Section -->
            <section class="py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <div class="mb-12 text-center">
                        <h2 class="mb-8 text-3xl font-bold md:text-4xl">Development <span class="text-primary">Status</span></h2>
                    </div>

                    <div class="glass-card p-8">
                        <div class="glow mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-hero">
                            <TrendingUp class="h-12 w-12 text-primary-foreground" />
                        </div>
                        <h3 class="mb-4 text-center text-2xl font-semibold">Early Development Phase</h3>
                        <p class="mb-6 text-center text-lg leading-relaxed text-muted-foreground">
                            The DigitalSafe project is currently in early development. Funds raised from token sales will be allocated to core
                            infrastructure development, team recruitment, MVP development, security audits, and marketing.
                        </p>

                        <div class="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                            <div class="rounded-lg bg-muted/20 p-4 text-center">
                                <h4 class="mb-2 font-semibold text-primary">Current Focus</h4>
                                <ul class="space-y-1 text-sm text-muted-foreground">
                                    <li>• Team recruitment and expansion</li>
                                    <li>• MVP development and testing</li>
                                    <li>• Security audits and compliance</li>
                                </ul>
                            </div>
                            <div class="rounded-lg bg-muted/20 p-4 text-center">
                                <h4 class="mb-2 font-semibold text-primary">Next Steps</h4>
                                <ul class="space-y-1 text-sm text-muted-foreground">
                                    <li>• Platform beta launch</li>
                                    <li>• Community building</li>
                                    <li>• Strategic partnerships</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    </div>
</template>
